import {
  AfterViewInit,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  DestroyRef,
  inject,
} from '@angular/core';
import { NgIf, formatDate } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom, Subscription } from 'rxjs';

import { MatAccordion, MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';

import { QuickSearchCriteria } from 'src/app/shared/entities/QuickSearchCriteria';
import { QuickSearchResults } from 'src/app/shared/entities/QuickSearchResults';
import { LotList } from 'src/app/shared/entities/LotList';
import { UpdateUserSettings } from 'src/app/shared/entities/UpdateUserSettings';
import { UserCredentialsFromApnLink } from 'src/app/shared/entities/UserCredentialsFromApnLink';
import {
  ISettingsState,
  ISearchInvoiceDataForInvoiceNotificationRequest,
  ISendInvoiceNotificationResponse,
  ISearchInvoiceDataForComplexInvoiceNotificationRequest,
} from 'src/app/shared/interfaces';

import { QueryMessageService } from 'src/app/core/services/broadcast/MessageDispatchingService';
import { LotService } from 'src/app/core/services/data/lot.service';
import { LoggingService } from 'src/app/core/services/helper/log.service';
import { AuthService } from 'src/app/core/services/auth-security/auth.service';
import { UserService } from 'src/app/core/services/auth-security/user.service';
import { InvoiceService } from 'src/app/core/services/data/invoice.service';
import { AdjustmentService } from 'src/app/core/services/data/adjustment.services';
import { QuicksearchService } from 'src/app/core/services/data/quicksearch.service';
import { DataService } from 'src/app/core/services/data/data.service';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { environment } from 'src/environments/environment';
import { InfoBannerComponent } from 'src/app/shared/ui/info-banner/info-banner.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SendInvoicesResultsComponent } from '../send-invoices-results/send-invoices-results.component';

@Component({
  selector: 'app-send-invoices-home-v2',
  standalone: true,
  imports: [
    NgIf,
    FormsModule,
    ReactiveFormsModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    SendInvoicesResultsComponent,
    InfoBannerComponent,
  ],
  templateUrl: './send-invoices-home-v2.component.html',
  styleUrl: './send-invoices-home-v2.component.scss',
})
export class SendInvoicesHomeV2Component implements OnInit, AfterViewInit, OnDestroy {
  urlDomain: string = '';
  quicksearchForm!: FormGroup;
  screenWidth: number = 1000;
  screenHeight: number;
  midScreenSize: number = 640;
  mobileScreenSize: number = 790;
  displayQuickSearch: boolean = true;
  displayQuickSearchSubscription: Subscription;
  quicksearchCriteriaForQuery: QuickSearchCriteria;
  queryLotNumberSelected: string = '';
  settingsStateValues: ISettingsState;
  invoiceSearchResults: ISendInvoiceNotificationResponse[] = [];
  hasProcessedInvoiceQuery: boolean = false;
  isInvoiceQueryInProgress: boolean = false;

  LotListByUserName: LotList[] = [];

  lotNumber = 'No Lot Number';
  cardNumber = '';
  parkerName = '';
  companyName = '';
  invoiceNumber = '';
  revenueStartDate = '';
  revenueEndDate = '';
  transactionStartDate = '';
  transactionEndDate = '';
  invoiceType = 'ALL';
  includeZeroBalanceInvoices = 'No';
  defaultLotNumber = '';
  defaultLot: LotList;
  isInProdSimulate: boolean = true;
  customerType: string = 'ALL';
  senderEmailAddress: string | null = null;

  userData: UserCredentialsFromApnLink | undefined;

  quickSearchCriteria: QuickSearchCriteria = new QuickSearchCriteria();
  invoiceSearchCriteria: ISearchInvoiceDataForComplexInvoiceNotificationRequest =
    {} as ISearchInvoiceDataForComplexInvoiceNotificationRequest;
  quickSearchResults: QuickSearchResults[] = [];
  lotDisplayName = 'No Lot Selected';
  displayQuickSearchRegion: boolean = true;
  theSearchCriteria = 'No Current Search Parms';
  enableMode: boolean = false;
  disableSearchButton: boolean = false;
  cookieAutoHideSearchCriteria: boolean = true;
  isSendInvoicesChildLoading: boolean = false;
  isSendInvoicesTabLoading: boolean = false;
  hasSendInvoicesFormRendered: boolean = false;
  sendInvoicesSpinnerStartTimestamp: number = 0;
  readonly sendInvoicesMinSpinnerMs: number = 300;

  _dataService = inject(DataService);
  _appGeneralStateService = inject(AppGeneralStateService);
  _destroyRef = inject(DestroyRef);

  constructor(
    private messageService: QueryMessageService,
    private lotService: LotService,
    public dialog: MatDialog,
    private userService: UserService,
    private authService: AuthService,
    private loggingService: LoggingService,
    private invoiceService: InvoiceService,
    private adjustmentService: AdjustmentService,
    private router: Router,
    private route: ActivatedRoute,
    private quickSearchService: QuicksearchService,
  ) {
    this.urlDomain = environment.UrlDomain;
  }

  @ViewChild(MatAccordion) accordion: MatAccordion;
  @ViewChild(MatExpansionPanel, { static: true }) matExpansionPanelElement: MatExpansionPanel;

  async ngOnInit(): Promise<void> {
    this.lotNumber = this._appGeneralStateService.currentLotNumber;
    this.initInvoiceSearchForm();

    this.senderEmailAddress = await this.getSenderEmail(this.lotNumber);
    if (this.senderEmailAddress == null || this.senderEmailAddress.trim() === '') {
      return;
    }

    this.setLotDisplayName();

    this._appGeneralStateService.lotNumberSubject.subscribe({
      next: (v) => this.onLotNumberChange(v),
    });
  }

  ngOnDestroy(): void {}

  initInvoiceSearchForm(): void {
    this.quicksearchForm = new FormGroup({
      lotNumber: new FormControl(this._appGeneralStateService.currentLotNumber, [Validators.required]),
      invoiceNumber: new FormControl('', []),
      parkerName: new FormControl('', []),
      customerType: new FormControl('ALL', []),
      companyName: new FormControl('', []),
      invoiceType: new FormControl('ALL', []),
      includeZeroBalances: new FormControl('OPEN', []),
      revenueStartDate: new FormControl('', []),
      revenueEndDate: new FormControl('', []),
      transactionStartDate: new FormControl('', []),
      transactionEndDate: new FormControl('', []),
    });

    // Explicitly apply defaults after form creation so mat-selects render a selected option on first paint.
    this.quicksearchForm.patchValue(
      {
        customerType: 'ALL',
        invoiceType: 'ALL',
        includeZeroBalances: 'OPEN',
      },
      { emitEvent: false },
    );
  }

  async doInvoiceSearch(): Promise<void> {
    if (this.quicksearchForm.valid) {
      this.invoiceSearchCriteria = this.getComplexQueryArgs();
      this.displayQuickSearchRegion = !this.cookieAutoHideSearchCriteria;

      // Balance Only query performs query by the invoice lot number and the current balance of the invoice, ignoring all other potential search criteria. This is used to power the "Send Invoices" feature, which needs to pull all invoices for a lot that have a balance due in order to send out invoice notifications.
      const queryTypeIsBalanceOnlyType = this.isLotNumberBalanceQuery(this.invoiceSearchCriteria);
      if (queryTypeIsBalanceOnlyType) {
        await this.getInvoicesNotificationSimpleBalanceList();
      } else {
        await this.getInvoicesNotificationListByComplexQuery();
      }
    }
  }

  // HTTP Call to get invoice list
  async getInvoicesNotificationSimpleBalanceList(): Promise<void> {
    this.isInvoiceQueryInProgress = true;
    try {
      const queryData: ISearchInvoiceDataForInvoiceNotificationRequest = {
        lotNumber: this._appGeneralStateService.currentLotNumber,
        queryType: this.getQueryBalanceEnumValue(),
      };
      this.invoiceSearchResults = (await this.getInvoiceListLotSearchByBalanceQueryType(queryData)) ?? [];
      this.hasProcessedInvoiceQuery = true;
    } finally {
      this.isInvoiceQueryInProgress = false;
    }
  }

  async getInvoicesNotificationListByComplexQuery(): Promise<void> {
    this.isInvoiceQueryInProgress = true;
    try {
      const queryData = this.getComplexQueryArgs();
      this.invoiceSearchResults = (await this.getInvoiceListLotSearchByComplexQuery(queryData)) ?? [];
      this.hasProcessedInvoiceQuery = true;
    } finally {
      this.isInvoiceQueryInProgress = false;
    }
  }

  // Check to see if this query is a simple lot number and balance query,
  // which is used for the "Send Invoices" feature to pull all invoices for a lot that have a balance due,
  // ignoring all other potential search criteria.
  isLotNumberBalanceQuery(queryData: ISearchInvoiceDataForComplexInvoiceNotificationRequest): boolean {
    const hasNoTextFilters =
      queryData.companyName === '' &&
      queryData.invoiceNumber === '' &&
      queryData.parkerName === '' &&
      queryData.revenueEndDate === '' &&
      queryData.revenueStartDate === '' &&
      //queryData.startDateForQuery === '' &&
      queryData.transactionEndDate === '' &&
      queryData.transactionStartDate === '';

    const hasDefaultTypes = queryData.customerType === 'ALL' && queryData.invoiceType === 'ALL';

    return hasNoTextFilters && hasDefaultTypes;
  }

  // Gets the enumerated value for the type of balance query
  getQueryBalanceEnumValue(): number {
    let enumQueryType: number = 1;

    const formValue = this.quicksearchForm.getRawValue();
    return this.setZeroBalanceQueryType(formValue.includeZeroBalances ?? 'OPEN');
  }

  getComplexQueryArgs(): ISearchInvoiceDataForComplexInvoiceNotificationRequest {
    const formValue = this.quicksearchForm.getRawValue();
    let qsBuildQuery: ISearchInvoiceDataForComplexInvoiceNotificationRequest =
      {} as ISearchInvoiceDataForComplexInvoiceNotificationRequest;
    qsBuildQuery.lotNumber = this._appGeneralStateService.currentLotNumber;
    qsBuildQuery.customerType = formValue.customerType ?? 'ALL';
    qsBuildQuery.companyName = formValue.companyName ?? '';
    qsBuildQuery.invoiceNumber = formValue.invoiceNumber ?? '';
    qsBuildQuery.invoiceType = formValue.invoiceType ?? 'ALL';
    qsBuildQuery.parkerName = formValue.parkerName ?? '';
    qsBuildQuery.revenueStartDate = this.formatAceDateTime(formValue.revenueStartDate ?? '');
    qsBuildQuery.revenueEndDate = this.formatAceDateTime(formValue.revenueEndDate ?? '');
    qsBuildQuery.transactionStartDate = this.formatAceDateTime(formValue.transactionStartDate ?? '');
    qsBuildQuery.transactionEndDate = this.formatAceDateTime(formValue.transactionEndDate ?? '');
    qsBuildQuery.queryType = this.getQueryBalanceEnumValue();

    return qsBuildQuery;
  }

  setLotDisplayName(): void {
    this.lotDisplayName =
      this._appGeneralStateService.currentLotNumber + ' - ' + this._appGeneralStateService.currentLotName;
  }

  setCurrentUserLot(lotNumber: string): void {
    this.lotNumber = this._appGeneralStateService.currentLotNumber;
    this.defaultLotNumber = this._appGeneralStateService.currentLotNumber;
    this.queryLotNumberSelected = this._appGeneralStateService.currentLotNumber;
  }

  ngAfterViewInit(): void {
    this.screenWidth = window.innerWidth;
    this.screenHeight = window.innerHeight;
    this.setCurrentUserLot(this.defaultLotNumber);
    this.displayQuickSearchRegion = this.quickSearchService.getQuickSearchCriteriaAutoHide();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.screenWidth = window.innerWidth;
    this.screenHeight = window.innerHeight;
  }

  routeValidatedUserToLanaiHome(userData: UserCredentialsFromApnLink): void {}

  setLotIdNameByLotNumber(lotNumber: string): void {}

  getApnLaunchLink(): string {
    let linkId: string = '';
    let tempLink: any = this.route.snapshot.paramMap.get('link');
    if (tempLink !== undefined || tempLink !== null) {
      linkId = tempLink;
    }
    return linkId;
  }

  setZeroBalanceQueryType(includeZeroBalanceInvoices: string): number {
    let enumQueryType: number = -1;
    switch (includeZeroBalanceInvoices.toUpperCase()) {
      case 'OPEN':
        enumQueryType = 1;
        break;
      case 'INCLUDE_ZERO_BALANCE':
        enumQueryType = 2;
        break;
      case 'ONLY ZERO BALANCES':
        enumQueryType = 3;
        break;
      case 'ONLY CREDIT BALANCES':
        enumQueryType = 4;
        break;
      case 'PAST DUE BALANCES':
        enumQueryType = 5;
        break;
      default:
        enumQueryType = 1;
        break;
    }
    return enumQueryType;
  }

  onEnableAdjustments(message: any): void {
    this.enableMode = true;
  }

  setQueryStartDate(quicksearchCriteria: QuickSearchCriteria): string {
    let queryStartDate: string = '';
    let qsCriteria: QuickSearchCriteria = quicksearchCriteria;
    let getStartDate: boolean = true;

    if (qsCriteria.customerType !== '') getStartDate = false;
    if (qsCriteria.companyName !== '') getStartDate = false;
    if (qsCriteria.invoiceNumber !== '') getStartDate = false;
    if (qsCriteria.parkerName !== '') getStartDate = false;
    if (qsCriteria.revenueEndDate !== '') getStartDate = false;
    if (qsCriteria.revenueStartDate !== '') getStartDate = false;
    if (qsCriteria.transactionEndDate !== '') getStartDate = false;
    if (qsCriteria.transactionStartDate !== '') getStartDate = false;

    if (getStartDate) {
      let dateOneYearBack: Date = new Date();
      dateOneYearBack.setDate(dateOneYearBack.getDate() - 365);
      queryStartDate = this.formatAceDateTime(dateOneYearBack.toString());
    }

    return queryStartDate;
  }

  formatAceDateTime(dateToPrepare: string): string {
    let formattedDate: string = '';
    if (dateToPrepare !== '') {
      formattedDate = formatDate(new Date(dateToPrepare), 'MM-dd-yyyy', 'en-US');
    }
    return formattedDate;
  }

  onLotNumberChange(lotNumber: any) {
    const lotNumberId = this.normalizeLotNumber(lotNumber);
    const currentLotNumber = this.normalizeLotNumber(this.lotNumber);

    // Ignore duplicate lot change events to avoid resetting criteria during searches.
    if (lotNumberId === currentLotNumber) {
      return;
    }

    this._appGeneralStateService.lotChangeMessage();
    this.displayQuickSearchRegion = true;
    this.doSearchCriteriaReset();

    if (lotNumberId === '-1') {
      this.queryLotNumberSelected = '';
      this.disableSearchButton = true;
    } else {
      this.queryLotNumberSelected = lotNumberId;
      this.lotNumber = lotNumberId;

      let userSettings: UpdateUserSettings = new UpdateUserSettings();
      userSettings.userId = this._appGeneralStateService.userId;
      userSettings.lotNumber = this.lotNumber;

      this.authService.setDefaultLotId(lotNumberId);
      this.lotService.setCurrentDefaultLotForUser(userSettings);
      this.defaultLotNumber = this.lotNumber;
    }

    this.setLotDisplayName();
  }

  private normalizeLotNumber(value: unknown): string {
    const raw = (value ?? '').toString().trim();

    if (raw === '' || raw === '-1') {
      return raw;
    }

    // Some callers can pass display text like "2511 - Lot Name".
    return raw.includes('-') ? raw.split('-')[0].trim() : raw;
  }

  // doSearch() {
  //   if (this.cookieAutoHideSearchCriteria) this.displayQuickSearchRegion = false;
  //   else this.displayQuickSearchRegion = true;

  //   this.quickSearchService.setQuickSearchCriteriaAutoHide(this.displayQuickSearchRegion);
  //   this.sendQueryMessage();
  // }

  setAutoHideSearchCriteriaOnNewSearchRequest() {}

  doSearchCriteriaReset() {
    console.trace('doSearchCriteriaReset called');
    this.quicksearchForm.patchValue({
      invoiceNumber: '',
      parkerName: '',
      customerType: 'ALL',
      companyName: '',
      invoiceType: 'ALL',
      includeZeroBalances: 'OPEN',
      revenueStartDate: '',
      revenueEndDate: '',
      transactionStartDate: '',
      transactionEndDate: '',
    });
  }

  onSendInvoicesLoadingStateChanged(isLoading: boolean): void {
    this.isSendInvoicesChildLoading = isLoading;

    if (isLoading && !this.sendInvoicesSpinnerStartTimestamp) {
      this.sendInvoicesSpinnerStartTimestamp = Date.now();
    }

    this.isSendInvoicesTabLoading = isLoading || !this.hasSendInvoicesFormRendered;
  }

  onSendInvoicesFormDisplayed(): void {
    this.hasSendInvoicesFormRendered = true;

    const elapsedMs = this.sendInvoicesSpinnerStartTimestamp
      ? Date.now() - this.sendInvoicesSpinnerStartTimestamp
      : this.sendInvoicesMinSpinnerMs;

    const remainingMs = Math.max(0, this.sendInvoicesMinSpinnerMs - elapsedMs);

    setTimeout(() => {
      this.isSendInvoicesTabLoading = false;
      this.sendInvoicesSpinnerStartTimestamp = 0;
    }, remainingMs);
  }

  setUserRoleIsAdmin(userName: string) {}

  getCurrentDefaultLotForThisUser(userName: string) {}

  getSavedLotNameForThisUser(lotNumber: string): string {
    return '';
  }

  isValidDate(dateString: string): boolean {
    let isValidDate: boolean = true;
    try {
      let newDate = new Date(dateString);
    } catch (error) {
      isValidDate = false;
    }
    return isValidDate;
  }

  private async getSenderEmail(lotNumber: string): Promise<string | null> {
    try {
      const email = await firstValueFrom(
        this._dataService.getInvoiceSenderEmailAddressForLot(lotNumber).pipe(takeUntilDestroyed(this._destroyRef)),
      );
      return email;
    } catch {
      return null;
    }
  }

  private async getInvoiceListLotSearchByBalanceQueryType(
    queryData: ISearchInvoiceDataForInvoiceNotificationRequest,
  ): Promise<ISendInvoiceNotificationResponse[] | null> {
    try {
      const invoiceList = await firstValueFrom(
        this._dataService
          .getInvoiceDataForInvoiceNotificationsByBalanceAndTypeQuery(queryData)
          .pipe(takeUntilDestroyed(this._destroyRef)),
      );
      return invoiceList;
    } catch {
      return null;
    }
  }

  private async getInvoiceListLotSearchByComplexQuery(
    queryData: ISearchInvoiceDataForComplexInvoiceNotificationRequest,
  ): Promise<ISendInvoiceNotificationResponse[] | null> {
    try {
      const invoiceList = await firstValueFrom(
        this._dataService
          .getInvoiceDataForInvoiceNotificationsByComplexQuery(queryData)
          .pipe(takeUntilDestroyed(this._destroyRef)),
      );
      return invoiceList;
    } catch {
      return null;
    }
  }
}
